<script lang="ts">
	/**
	 * A horizontal bar showing how much of a licence's seat allowance is taken.
	 *
	 * The bar turns amber as the last seats go and red once none are left, because
	 * "9 of 10" and "10 of 10" are one character apart in text and mean entirely
	 * different things to someone about to activate a machine.
	 */

	import { t } from '../i18n';

	interface Props {
		/** Seats taken. */
		used: number;
		/** Seats the licence grants. */
		total: number;
		/** Render the numbers above the bar. */
		showLabel?: boolean;
		/** Extra classes for the wrapper. */
		class?: string;
	}

	let { used, total, showLabel = true, class: className = '' }: Props = $props();

	/** Fill percentage, clamped so a licence over its allowance still renders a full bar. */
	let percent = $derived(total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0);

	/** Bar colour: green with room to spare, amber on the last fifth, red when full. */
	let barClass = $derived(percent >= 100 ? 'bg-red-500' : percent >= 80 ? 'bg-yellow-500' : 'bg-green-500');
</script>

<div class={className}>
	{#if showLabel}
		<div class="mb-1 flex items-baseline justify-between gap-2">
			<span class="text-sm font-semibold text-gray-900 dark:text-white">
				{used} / {total}
			</span>
			<span class="text-xs text-gray-500 dark:text-gray-400">
				{$t('{{count}} free', { count: Math.max(0, total - used) })}
			</span>
		</div>
	{/if}
	<div class="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
		<div class="h-full rounded-full transition-all {barClass}" style="width: {percent}%"></div>
	</div>
</div>
